import { isRecord, jsonObject, nonEmptyString } from "../../shared/json-body";
import { type RoutingFacts, skuById, skuIdOf, skusOfModel } from "../../shared/pricing";
import type { TransportRefusal } from "../../shared/transport";

/** The most output a request may ask for when no endpoint of its model names a limit. */
const MAX_OUTPUT_TOKENS = 128_000;

/**
 * What an OpenRouter request asked for. The upstream is pinned only when `provider.only` names one
 * endpoint and fallbacks are off; otherwise OpenRouter chooses. A request that names no tier runs at
 * OpenRouter's `default`.
 */
export function openrouterRequestedFacts(requestBody: string): Partial<RoutingFacts> {
  const body = jsonObject(requestBody);
  const provider = isRecord(body.provider) ? body.provider : {};
  const only = Array.isArray(provider.only) ? provider.only : [];
  const pinned =
    only.length === 1 && provider.allow_fallbacks === false ? nonEmptyString(only[0]) : undefined;
  const model = nonEmptyString(body.model);
  return {
    ...(model === undefined ? {} : { model }),
    upstream: pinned ?? "auto",
    tier: nonEmptyString(body.service_tier) ?? "default",
  };
}

/**
 * The OpenRouter SKU a call is billed at: the endpoint it pinned, or the model's `auto` SKU when it
 * leaves the endpoint to OpenRouter, priced at the cost OpenRouter reports.
 */
export function openrouterSkuOf({
  model,
  upstream,
  tier,
}: Partial<RoutingFacts>): string | undefined {
  if (model === undefined || upstream === undefined || tier === undefined) return;
  return skuIdOf({ billedBy: "openrouter", model, endpoint: upstream, tier });
}

/**
 * The call needs a SKU for what it asked for, with room for the output it asked for, and every
 * fallback model in `models` needs an OpenRouter SKU too.
 */
export function openrouterRefusal(
  requested: Partial<RoutingFacts>,
  fallbacks: string[],
  maxTokens: number | undefined,
): TransportRefusal | null {
  if (maxTokens === undefined)
    return { refusal: "invalid_request", message: "max_tokens is required." };
  const sku = skuById(openrouterSkuOf(requested));
  if (!sku)
    return {
      refusal: "model_not_found",
      message: `Pax has no price for ${requested.model} on ${requested.upstream} at the ${requested.tier} tier.`,
    };
  const limit = sku.maxOutputTokens ?? MAX_OUTPUT_TOKENS;
  if (maxTokens > limit)
    return {
      refusal: "invalid_request",
      message: `max_tokens is ${maxTokens}, above ${requested.model}'s limit of ${limit}.`,
    };
  const unpriced = fallbacks.find(model => skusOfModel("openrouter", model).length === 0);
  if (unpriced) return { refusal: "model_not_found", message: `Pax has no price for ${unpriced}.` };
  return null;
}
